const SOLAR_PRICE = {
  'Sonnenkollektoren 2': 2870,
  'Sonnenkollektoren 4': 3240,
  'Sonnenkollektoren 6': 3610,
  'Sonnenkollektoren 9': 4165,
};

const PAINT_LABELS = {
  materialsklall: 'Profilfarbe',
  materiallamellensklall: 'Lamellenfarbe',
  materialsglall: 'Profilfarbe',
  zipmaterialsklside: 'ZIP-Stoff Seite',
  zipmaterialsglside: 'ZIP-Stoff Glas-Seite',
  zipmaterialsgltop: 'ZIP-Stoff Glas-Dach',
};

const stripNum = (s) => Number(String(s).replace(/\D/g, '')) || 0;

const textureToName = (path) => {
  if (!path) return null;
  const base = path.split('/').pop().replace(/\.[a-z0-9]+$/i, '');
  return base.replace(/_/g, ' ').trim();
};

const massVar = (vars, name) => {
  const v = vars?.find((x) => x.VariableName === name);
  return v != null ? Number(v.VariableValue) : null;
};

const formatMasseFromVars = (id, vars) => {
  if (!vars?.length) return null;
  const breite = massVar(vars, 'Breite');
  const tiefe = massVar(vars, 'Tiefe');
  const hoehe = massVar(vars, 'Hoehe');
  const hoeheOKT = massVar(vars, 'HoeheOKT');
  const hoeheUKT = massVar(vars, 'HoeheUKT');
  const hoeheOKW = massVar(vars, 'HoeheOKW');

  if (id === '1547') {
    const nurSeiten = massVar(vars, 'NurSeiten');
    if (tiefe == null) return null;
    if (nurSeiten === 1) {
      return `${tiefe * 2 * 10} mm Profillaenge`;
    }
    if (breite == null) return null;
    return `${(breite * 2 + tiefe * 2) * 10} mm Profillaenge`;
  }

  if (id === '1250') {
    if (breite == null || tiefe == null) return null;
    const sparren = massVar(vars, 'Sparren');
    const ledProSparren = massVar(vars, 'LEDproSparren');
    const segments = [`${breite * 10}x${tiefe * 10} mm`];
    if (sparren != null) segments.push(`Sparren: ${sparren}`);
    if (ledProSparren != null) segments.push(`LEDproSparren: ${ledProSparren}`);
    return segments.join(', ');
  }

  const parts = [];
  if (breite != null) parts.push(breite * 10);
  if (tiefe != null) parts.push(tiefe * 10);

  if (id === '1001' && hoeheUKT != null && hoeheOKW != null) {
    parts.push(`${hoeheUKT * 10}/${hoeheOKW * 10}`);
  } else if (hoeheOKT != null) {
    parts.push(hoeheOKT * 10);
  } else if (hoehe != null) {
    parts.push(hoehe * 10);
  } else if (hoeheUKT != null) {
    parts.push(hoeheUKT * 10);
  }

  if (parts.length < 2) return null;
  const segments = [`${parts.join('x')} mm`];

  const ffAnzahl = massVar(vars, 'FFAnzahlElemente');
  if (ffAnzahl != null) segments.push(`FFAnzahlElemente: ${ffAnzahl}`);

  return segments.join(', ');
};

const BASIS_IDS = new Set(['1061', '1064', '1484', '1001']);

export const buildOrderPayload = ({
  order,
  orderSolar,
  paint,
  getPrice,
  getName,
  getPHours,
  getIHours,
}) => {
  const all = [...(order ?? []), ...(orderSolar ?? [])];

  const roof = all.find((i) => i.id === '1001');
  const roofW = roof?.Variables?.[0]?.VariableValue ?? 0;
  const roofD = roof?.Variables?.[1]?.VariableValue ?? 0;
  const roofAreaM2 = (roofW * roofD) / 10000;
  const roofGlassMasse =
    roofW && roofD ? `${roofW * 10}x${roofD * 10} mm` : null;

  const items = all
    .filter((i) => i.id !== '1005' && i.id !== '1008')
    .map((item) => {
      const isSolar = SOLAR_PRICE[item.id] != null;
      const isRoofGlass = item.id === '1055';
      const sideArea = item.squar ?? null;
      const areaM2 = isRoofGlass ? roofAreaM2 : sideArea;

      let baseTotal = 0;
      let unitPriceM2 = null;

      if (isSolar) {
        baseTotal = SOLAR_PRICE[item.id] * (item.qty ?? 1);
      } else if (isRoofGlass) {
        baseTotal = stripNum(getPrice(item.id, roofAreaM2));
        unitPriceM2 = roofAreaM2 ? Math.round(baseTotal / roofAreaM2) : null;
      } else {
        baseTotal = stripNum(
          getPrice(
            item.id,
            item.qty,
            item.side,
            item.section,
            undefined,
            item.sectionLenght,
            item.squar
          )
        );
        if (sideArea) {
          unitPriceM2 = Math.round(baseTotal / Math.max(item.qty ?? 1, 1));
          baseTotal *= sideArea;
        }
      }

      const masse = isRoofGlass
        ? roofGlassMasse
        : formatMasseFromVars(item.id, item.Variables);
      const baseName = isSolar ? item.id : getName(item.id);
      const isBasis = BASIS_IDS.has(item.id);
      return {
        id: item.id,
        name: isBasis && masse ? `${baseName} — ${masse}` : baseName,
        konfig_masse: masse,
        qty: item.qty ?? 1,
        side: item.side ?? null,
        section: item.section ?? null,
        area_m2: areaM2,
        unit_price_m2_chf: unitPriceM2,
        base_total_chf: Math.round(baseTotal),
        phours: isSolar ? 0 : getPHours(item.id),
        ihours: isSolar ? 0 : getIHours(item.id),
        Variables: item.Variables ?? null,
      };
    });

  const montageRate = stripNum(getPrice('1005', 1));
  const planungRate = stripNum(getPrice('1008', 1));

  const montageTotalHours = items.reduce(
    (acc, i) => acc + i.ihours * i.qty,
    0
  );
  const planungTotalHours = items.reduce(
    (acc, i) => acc + i.phours * i.qty,
    0
  );

  const totalPrice =
    items.reduce((acc, i) => acc + i.base_total_chf, 0) +
    montageRate * montageTotalHours +
    planungRate * planungTotalHours;

  const colors = Object.entries(paint ?? {})
    .filter(([key, val]) => PAINT_LABELS[key] && val)
    .map(([key, val]) => ({
      label: PAINT_LABELS[key],
      name: textureToName(val),
    }))
    .filter((c) => c.name);

  return {
    order: items,
    montage: { rate_chf: montageRate, total_hours: montageTotalHours },
    planung: { rate_chf: planungRate, total_hours: planungTotalHours },
    colors,
    totalPrice,
  };
};
